import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { IconRefresh } from "@tabler/icons-react";

import PageHeader from "@/components/common/PageHeader";
import { StatusText } from "@/components/dashboard/dashboard-card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { TableCard } from "@/components/ui/table-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  disconnectRecharge,
  getRechargeAvailability,
  getRechargeOauthUrl,
  getRechargeRetention,
  getRechargeStatus,
  RECHARGE_TICKET_KEY,
  type RechargeWebhookProblem,
} from "@/services/recharge.service";

const errMessage = (e: unknown) =>
  (e as AxiosError<{ message: string }>)?.response?.data?.message ||
  (e instanceof Error ? e.message : "Something went wrong");

/** Plain-language reason for a failed webhook registration, by problem class. */
const webhookProblemText = (problem: RechargeWebhookProblem | null) => {
  switch (problem) {
    case "UNAUTHORIZED":
      return "Recharge did not allow Reveshare to subscribe to events on this store. Check that the Reveshare app has access to orders and subscriptions in your Recharge settings, then reconnect.";
    case "REJECTED":
      return "Recharge did not accept the subscription request. Reconnect to try again; if it keeps failing, contact support and include the technical details below.";
    case "UNREACHABLE":
      return "Recharge could not be reached while setting up event subscriptions. Reconnect to try again.";
    default:
      return "Reveshare could not finish setting up event subscriptions. Reconnect to try again.";
  }
};

const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

/**
 * The brand's Recharge connection and what it has produced: which affiliates
 * bring subscribers who stay. For stores that do not sell subscriptions this
 * page is simply a card that never gets connected.
 */
export default function RechargePage() {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);

  const { data: availability } = useQuery({
    queryKey: ["recharge-availability"],
    queryFn: getRechargeAvailability,
    staleTime: 5 * 60_000,
  });
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery({ queryKey: ["recharge-status"], queryFn: getRechargeStatus });
  const connected = Boolean(status?.connected);

  const { data: retention, isLoading: retentionLoading } = useQuery({
    queryKey: ["recharge-retention"],
    queryFn: getRechargeRetention,
    enabled: connected,
  });

  const { mutate: disconnect, isPending: disconnecting } = useMutation({
    mutationFn: disconnectRecharge,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["recharge-status"] });
      toast.success("Recharge disconnected");
    },
    onError: (e) => toast.error(errMessage(e)),
  });

  const connect = async () => {
    setConnecting(true);
    try {
      const { url, ticket } = await getRechargeOauthUrl();
      // Our stand-in for OAuth `state`: this tab, and only this tab, can
      // finish the install (Integrations.tsx claims it on return).
      try {
        sessionStorage.setItem(RECHARGE_TICKET_KEY, ticket);
      } catch {
        toast.error("Your browser blocked session storage; the connection cannot be completed.");
        setConnecting(false);
        return;
      }
      window.location.href = url;
    } catch (e) {
      toast.error(errMessage(e));
      setConnecting(false);
    }
  };

  const statusTone = statusLoading
    ? null
    : status?.status === "NEEDS_RECONNECT"
      ? { tone: "SUSPENDED", label: "Needs reconnecting" }
      : connected
        ? { tone: "ACTIVE", label: "Connected" }
        : { tone: "INACTIVE", label: "Not connected" };

  const webhooksIncomplete =
    connected && status && status.webhooks.registered < status.webhooks.expected;

  return (
    <div className="space-y-6 p-4">
      <PageHeader
        title="Recharge"
        description="Pay affiliates on subscription renewals and see which of them bring subscribers who stay."
      />

      <div className="text-sm">
        <Link to="/brand/integrations" className="text-muted-foreground hover:text-foreground">
          ← All integrations
        </Link>
      </div>

      {availability && !availability.available && (
        <Alert variant="warning">
          <AlertDescription>
            Recharge is not enabled on this Reveshare environment yet.
          </AlertDescription>
        </Alert>
      )}
      {statusError && (
        <Alert variant="destructive">
          <AlertDescription>{errMessage(statusError)}</AlertDescription>
        </Alert>
      )}

      {/* Connection */}
      <section className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
              <IconRefresh className="size-5 text-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">Connection</h3>
              <div className="mt-0.5 text-xs">
                {statusTone === null ? (
                  <span className="text-muted-foreground">Checking…</span>
                ) : (
                  <StatusText status={statusTone.tone} label={statusTone.label} />
                )}
              </div>
              {connected && status?.myshopifyDomain && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Store {status.myshopifyDomain}
                  {status.connectedAt &&
                    `, connected ${new Date(status.connectedAt).toLocaleDateString()}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {connected ? (
              <>
                <Button size="sm" variant="outline" onClick={connect} disabled={connecting}>
                  Reconnect
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => disconnect()}
                  disabled={disconnecting}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={connect}
                disabled={connecting || statusLoading || availability?.available === false}
              >
                {status?.status === "NEEDS_RECONNECT" ? "Reconnect" : "Connect Recharge"}
              </Button>
            )}
          </div>
        </div>

        {!connected && !statusLoading && (
          <p className="mt-4 max-w-prose text-sm text-muted-foreground">
            For stores selling subscriptions through Recharge. Today an affiliate earns on a
            subscriber's first order only. Connected, Reveshare links each subscription to the
            affiliate who brought it and pays the renewal rate you set on each program, until
            the cap you choose or the customer cancels. You approve the connection in your
            Recharge admin; Reveshare only reads charges, subscriptions and discounts.
          </p>
        )}

        {connected && status && status.missingScopes.length > 0 && (
          <Alert variant="warning" className="mt-4">
            <AlertDescription>
              The Reveshare app in Recharge is missing permissions ({status.missingScopes.join(", ")}).
              Renewals may not be tracked until this is fixed on Reveshare's side.
            </AlertDescription>
          </Alert>
        )}
        {webhooksIncomplete && (
          <Alert variant="warning" className="mt-4">
            <AlertDescription>
              <p>
                Recharge is connected, but renewals are not being tracked yet: only{" "}
                {status!.webhooks.registered} of {status!.webhooks.expected} event subscriptions
                are in place.
              </p>
              <p className="mt-1">{webhookProblemText(status!.webhooks.problem)}</p>
              {status!.webhooks.error && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs opacity-80">Technical details</summary>
                  <code className="mt-1 block break-all text-xs opacity-80">
                    {status!.webhooks.error}
                  </code>
                </details>
              )}
            </AlertDescription>
          </Alert>
        )}
        {connected && (
          <p className="mt-4 text-xs text-muted-foreground">
            Set the renewal rate per program under{" "}
            <Link to="/brand/programs" className="font-medium text-brand-700 hover:underline">
              Programs
            </Link>{" "}
            › Subscription renewals.
          </p>
        )}
      </section>

      {/* Retention */}
      {connected && (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Retention by affiliate</h3>
            <p className="text-sm text-muted-foreground">
              {retentionLoading || !retention
                ? "Loading…"
                : retention.totals.subscriptions === 0
                  ? "No attributed subscriptions yet. They appear here after the first subscription checkout with an affiliate code."
                  : `${retention.totals.activeSubscriptions} of ${retention.totals.subscriptions} attributed subscriptions still active (${retention.totals.retentionRate}%), ${retention.totals.renewals} renewals worth ${money(retention.totals.renewalRevenue)} so far.`}
            </p>
          </div>

          {retention && retention.affiliates.length > 0 ? (
            <TableCard className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Affiliate</TableHead>
                    <TableHead className="text-right">Active</TableHead>
                    <TableHead className="text-right">Cancelled</TableHead>
                    <TableHead className="text-right">Retention</TableHead>
                    <TableHead className="text-right">Renewals</TableHead>
                    <TableHead className="text-right">Renewal revenue</TableHead>
                    <TableHead className="text-right">Paid out</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {retention.affiliates.map((a) => (
                    <TableRow key={a.affiliateId}>
                      <TableCell>
                        <div className="font-medium text-foreground">
                          {a.firstName} {a.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">{a.email}</div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{a.activeSubscriptions}</TableCell>
                      <TableCell className="text-right tabular-nums">{a.cancelledSubscriptions}</TableCell>
                      <TableCell className="text-right tabular-nums">{a.retentionRate}%</TableCell>
                      <TableCell className="text-right tabular-nums">{a.renewals}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(a.renewalRevenue)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(a.renewalCommission)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableCard>
          ) : (
            !retentionLoading && (
              <EmptyState
                title="Nothing to show yet"
                bordered
                body="Retention fills in as subscribers referred by your affiliates renew or cancel."
              />
            )
          )}
        </section>
      )}
    </div>
  );
}
